import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, FileDown, DollarSign, X, Mail, BadgeCheck, FileText, Send, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, TableShell, EmptyRow, StatusBadge } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { OrderPhotosDialog } from "../components/OrderPhotosDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUSES = ["open", "in_progress", "completed", "paid"];
const ADAS_SERVICE_NAMES = {
  static: "ADAS Static Calibration",
  dynamic: "ADAS Dynamic Calibration",
  dual: "ADAS Dual Calibration",
};
const emptyForm = {
  customer_id: "", vehicle_id: "", status: "open", notes: "", items: [],
  technician: "", insurance_company: "", claim_number: "", deductible: "",
  adas_type: "none", adas_status: "pending", adas_notes: "", scheduled_at: "",
};

export default function WorkOrders() {
  const { user, t, fmt } = useApp();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const [photosOrder, setPhotosOrder] = useState(null);
  const [payment, setPayment] = useState({ amount: "", method: "cash" });
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = () =>
    api.get("/work-orders").then((r) => {
      setRows(r.data);
      setPhotosOrder((prev) => (prev ? r.data.find((x) => x.id === prev.id) || null : null));
    }).catch(() => {});
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm({ ...emptyForm, items: [] }); setEditId(null); setOpen(true); };
  const openEdit = (o) => {
    setForm({
      customer_id: o.customer_id, vehicle_id: o.vehicle_id || "", status: o.status,
      notes: o.notes || "", items: o.items.map((i) => ({ ...i })),
      technician: o.technician || "", insurance_company: o.insurance_company || "",
      claim_number: o.claim_number || "", deductible: o.deductible ? String(o.deductible) : "",
      adas_type: o.adas_type || "none",
      adas_status: ["pending", "completed"].includes(o.adas_status) ? o.adas_status : "pending",
      adas_notes: o.adas_notes || "", scheduled_at: o.scheduled_at || "",
    });
    setEditId(o.id); setOpen(true);
  };

  const addServiceItem = (sid) => {
    const s = services.find((x) => x.id === sid);
    if (!s) return;
    setForm({ ...form, items: [...form.items, { description: s.name, quantity: 1, unit_price: s.price }] });
  };
  const addBlankItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const setItem = (idx, key, val) => {
    const items = form.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
    setForm({ ...form, items });
  };
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const setAdasType = (v) => {
    const adasNames = Object.values(ADAS_SERVICE_NAMES);
    let items = form.items.filter((it) => !adasNames.includes(it.description));
    if (v !== "none") {
      const svc = services.find((s) => s.name === ADAS_SERVICE_NAMES[v]);
      items = [...items, { description: ADAS_SERVICE_NAMES[v], quantity: 1, unit_price: svc ? svc.price : 0 }];
    }
    setForm({ ...form, adas_type: v, items });
  };

  const total = form.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const selCust = customers.find((c) => c.id === form.customer_id);

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error(t("required")); return; }
    const payload = {
      ...form,
      deductible: parseFloat(form.deductible) || 0,
      adas_type: form.adas_type === "none" ? "" : form.adas_type,
      items: form.items.map((i) => ({ description: i.description, quantity: parseFloat(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
    };
    try {
      if (editId) await api.put(`/work-orders/${editId}`, payload);
      else await api.post("/work-orders", payload);
      toast.success(t("saved")); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/work-orders/${id}`); toast.success(t("deleted")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/work-orders/${id}/status`, { status }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/work-orders/${payOrder.id}/payments`, { amount: parseFloat(payment.amount), method: payment.method });
      toast.success(t("paymentRegistered")); setPayOrder(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const [sendingId, setSendingId] = useState(null);
  const emailReceipt = async (o) => {
    setSendingId(o.id);
    try {
      const { data } = await api.post(`/work-orders/${o.id}/send-receipt`);
      toast.success(`${t("emailSent")} ${data.to}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSendingId(null); }
  };

  const emailQuote = async (o) => {
    setSendingId(o.id);
    try {
      const { data } = await api.post(`/work-orders/${o.id}/send-quote`);
      toast.success(`${t("quoteSent")} ${data.to}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSendingId(null); }
  };

  const downloadQuote = async (o) => {
    try {
      const res = await api.get(`/work-orders/${o.id}/quote`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `quote-${o.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const downloadCertificate = async (o) => {
    try {
      const res = await api.get(`/work-orders/${o.id}/calibration-certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `calibration-CAL-${o.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const downloadReceipt = async (o) => {
    try {
      const res = await api.get(`/work-orders/${o.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${o.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  return (
    <div>
      <PageHeader title={t("workOrders")}>
        <Button data-testid="add-order-button" onClick={openNew} className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
          <Plus size={18} className="mr-2" /> {t("newOrder")}
        </Button>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
              filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {s === "all" ? t("all") : s === "paid" ? t("paid_status") : t(s)}
          </button>
        ))}
      </div>
      <TableShell headers={[t("order"), t("customer"), t("vehicle"), t("total"), t("balance"), t("status"), t("actions")]}>
        {filtered.length === 0 && <EmptyRow colSpan={7} />}
        {filtered.map((o) => (
          <tr key={o.id} data-testid={`order-row-${o.order_number}`} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-semibold text-slate-900">
              {o.order_number}
              {o.adas_type && (
                <span data-testid={`adas-badge-${o.order_number}`}
                  className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                    o.adas_status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  ADAS
                </span>
              )}
            </td>
            <td className="px-4 py-3">{o.customer_name}</td>
            <td className="px-4 py-3 text-slate-500">{o.vehicle} {o.plate && `· ${o.plate}`}</td>
            <td className="px-4 py-3 font-medium">{fmt(o.total)}</td>
            <td className="px-4 py-3">{fmt(o.total - o.paid_amount)}</td>
            <td className="px-4 py-3">
              <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                <SelectTrigger data-testid={`status-select-${o.order_number}`} className="w-36 h-8 text-xs border-0 bg-transparent p-0 focus:ring-0">
                  <StatusBadge status={o.status} />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s === "paid" ? t("paid_status") : t(s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </td>
            <td className="px-4 py-3">
              <div className="flex gap-1">
                <Button data-testid={`pay-order-${o.order_number}`} variant="ghost" size="icon" title={t("registerPayment")}
                  className="text-emerald-600 hover:text-emerald-800"
                  onClick={() => { setPayOrder(o); setPayment({ amount: String((o.total - o.paid_amount).toFixed(2)), method: "cash" }); }}>
                  <DollarSign size={15} />
                </Button>
                <Button data-testid={`quote-order-${o.order_number}`} variant="ghost" size="icon" title={t("quotePdf")}
                  className="text-slate-500 hover:text-slate-800" onClick={() => downloadQuote(o)}>
                  <FileText size={15} />
                </Button>
                <Button data-testid={`email-quote-${o.order_number}`} variant="ghost" size="icon" title={t("emailQuote")}
                  disabled={sendingId === o.id} className="text-indigo-500 hover:text-indigo-700" onClick={() => emailQuote(o)}>
                  <Send size={15} />
                </Button>
                <Button data-testid={`receipt-order-${o.order_number}`} variant="ghost" size="icon" title={t("downloadReceipt")}
                  className="text-sky-600 hover:text-sky-800" onClick={() => downloadReceipt(o)}>
                  <FileDown size={15} />
                </Button>
                <Button data-testid={`email-receipt-${o.order_number}`} variant="ghost" size="icon" title={t("emailReceipt")}
                  disabled={sendingId === o.id} className="text-violet-600 hover:text-violet-800" onClick={() => emailReceipt(o)}>
                  <Mail size={15} />
                </Button>
                {o.adas_type && (
                  <Button data-testid={`certificate-order-${o.order_number}`} variant="ghost" size="icon" title={t("certificate")}
                    className="text-amber-600 hover:text-amber-800" onClick={() => downloadCertificate(o)}>
                    <BadgeCheck size={15} />
                  </Button>
                )}
                <Button data-testid={`photos-order-${o.order_number}`} variant="ghost" size="icon" title={t("photos")}
                  className="text-teal-600 hover:text-teal-800" onClick={() => setPhotosOrder(o)}>
                  <Camera size={15} />
                </Button>
                <Button data-testid={`edit-order-${o.order_number}`} variant="ghost" size="icon" onClick={() => openEdit(o)}>
                  <Pencil size={15} />
                </Button>
                {user?.role === "admin" && (
                  <Button data-testid={`delete-order-${o.order_number}`} variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => remove(o.id)}>
                    <Trash2 size={15} />
                  </Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </TableShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? t("editOrder") : t("newOrder")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("customer")} *</Label>
              <Select value={form.customer_id}
                onValueChange={(v) => setForm({ ...form, customer_id: v, vehicle_id: customers.find((c) => c.id === v)?.vehicles?.[0]?.id || "" })}>
                <SelectTrigger data-testid="order-customer-select" className="h-11"><SelectValue placeholder={t("selectCustomer")} /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.vehicles?.[0]?.vehicle ? ` — ${c.vehicles[0].vehicle}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selCust && (selCust.vehicles || []).length > 0 && (
              <div className="space-y-1.5">
                <Label>{t("vehicle")}</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger data-testid="order-vehicle-select" className="h-11"><SelectValue placeholder={t("selectVehicle")} /></SelectTrigger>
                  <SelectContent>
                    {selCust.vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {[v.vehicle, v.year, v.color, v.plate].filter(Boolean).join(" · ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("items")}</Label>
                <div className="flex gap-2">
                  <Select value="" onValueChange={addServiceItem}>
                    <SelectTrigger data-testid="order-add-service-select" className="h-9 w-48 text-xs"><SelectValue placeholder={t("selectService")} /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name} — {fmt(s.price)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button data-testid="order-add-blank-item" type="button" variant="outline" size="sm" className="h-9" onClick={addBlankItem}>
                    <Plus size={14} className="mr-1" /> {t("addItem")}
                  </Button>
                </div>
              </div>
              {form.items.map((it, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input data-testid={`item-desc-${idx}`} placeholder={t("description")} value={it.description}
                    onChange={(e) => setItem(idx, "description", e.target.value)} className="h-10 flex-1" />
                  <Input data-testid={`item-qty-${idx}`} type="number" step="any" min="0" value={it.quantity}
                    onChange={(e) => setItem(idx, "quantity", e.target.value)} className="h-10 w-20" placeholder={t("qty")} />
                  <Input data-testid={`item-price-${idx}`} type="number" step="0.01" min="0" value={it.unit_price}
                    onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="h-10 w-28" placeholder={t("unitPrice")} />
                  <button type="button" data-testid={`item-remove-${idx}`} className="text-red-400 hover:text-red-600 p-1" onClick={() => removeItem(idx)}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              <div className="text-right font-heading font-bold text-lg text-slate-900" data-testid="order-total">
                {t("total")}: {fmt(total)}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="order-status-select" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s === "paid" ? t("paid_status") : t(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("notes")}</Label>
                <Input data-testid="order-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-11" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>{t("scheduledAt")}</Label>
                <Input data-testid="order-scheduled-input" type="datetime-local" value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="h-11" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/50">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("insuranceSection")}</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("technician")}</Label>
                  <Input data-testid="order-technician-input" value={form.technician}
                    onChange={(e) => setForm({ ...form, technician: e.target.value })} className="h-10" placeholder={user?.name} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("insuranceCompany")}</Label>
                  <Input data-testid="order-insurance-input" value={form.insurance_company}
                    onChange={(e) => setForm({ ...form, insurance_company: e.target.value })} className="h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("claimNumber")}</Label>
                  <Input data-testid="order-claim-input" value={form.claim_number}
                    onChange={(e) => setForm({ ...form, claim_number: e.target.value })} className="h-10" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("deductible")}</Label>
                  <Input data-testid="order-deductible-input" type="number" step="0.01" min="0" value={form.deductible}
                    onChange={(e) => setForm({ ...form, deductible: e.target.value })} className="h-10" />
                </div>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/50">
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{t("adas")}</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("adasType")}</Label>
                  <Select value={form.adas_type} onValueChange={setAdasType}>
                    <SelectTrigger data-testid="order-adas-type-select" className="h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("noAdas")}</SelectItem>
                      <SelectItem value="static">{t("static")}</SelectItem>
                      <SelectItem value="dynamic">{t("dynamic")}</SelectItem>
                      <SelectItem value="dual">{t("dual")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.adas_type !== "none" && (
                  <div className="space-y-1.5">
                    <Label>{t("adasStatus")}</Label>
                    <Select value={form.adas_status} onValueChange={(v) => setForm({ ...form, adas_status: v })}>
                      <SelectTrigger data-testid="order-adas-status-select" className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t("pending")}</SelectItem>
                        <SelectItem value="completed">{t("completed")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.adas_type !== "none" && (
                  <div className="col-span-2 space-y-1.5">
                    <Label>{t("adasNotes")}</Label>
                    <Input data-testid="order-adas-notes-input" value={form.adas_notes}
                      onChange={(e) => setForm({ ...form, adas_notes: e.target.value })} className="h-10" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="order-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <OrderPhotosDialog order={photosOrder} onOpenChange={() => setPhotosOrder(null)} onChanged={load} />

      <Dialog open={!!payOrder} onOpenChange={(v) => !v && setPayOrder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{t("registerPayment")} — {payOrder?.order_number}</DialogTitle></DialogHeader>
          <form onSubmit={submitPayment} className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("amount")} *</Label>
              <Input data-testid="payment-amount-input" required type="number" step="0.01" min="0.01" value={payment.amount}
                onChange={(e) => setPayment({ ...payment, amount: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("method")}</Label>
              <Select value={payment.method} onValueChange={(v) => setPayment({ ...payment, method: v })}>
                <SelectTrigger data-testid="payment-method-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "card", "transfer", "check"].map((m) => (
                    <SelectItem key={m} value={m}>{t(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setPayOrder(null)}>{t("cancel")}</Button>
              <Button data-testid="payment-save-button" type="submit" className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
